import {
  NOTIFICATIONS_ALL_API_URL,
  NOTIFICATIONS_UNREAD_COUNT_API_URL,
  NOTIFICATIONS_COUNTS_API_URL,
  NOTIFICATIONS_READ_API_URL,
  NOTIFICATION_DELETE_API_URL,
} from "../config/constants";
import { apiFetch } from "../utils/apiClient";


// ─── Real-time refresh signal ─────────────────────────────────────────────────

/**
 * Browser event fired after any action that the backend turns into a
 * notification (PROJECT_CREATED, INVOICE_SUBMITTED, REVIEW_ADDED, USER_*, etc.).
 * The Redux-backed `useNotifications` hook listens for this and refetches so the
 * new notification shows up without a manual page refresh.
 */
export const NOTIFICATIONS_REFRESH_EVENT = "notifications-refresh";

/** Call right after a mutation that produces a server-side notification. */
export const requestNotificationsRefresh = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(NOTIFICATIONS_REFRESH_EVENT));
  }
};

// ─── Backend DTO ──────────────────────────────────────────────────────────────



// ─── Auth helper ──────────────────────────────────────────────────────────────

const normalizeToken = (rawToken) =>
  rawToken
    .replace(/^"|"$/g, "")
    .replace(/^Bearer\s+/i, "")
    .trim();

const getAuthHeader = () => {
  const rawToken =
    sessionStorage.getItem("token") ||
    sessionStorage.getItem("authToken") ||
    sessionStorage.getItem("accessToken");

  if (!rawToken) {
    throw new Error("Authentication token not found. Please log in again.");
  }

  const tokenType = sessionStorage.getItem("tokenType") || "Bearer";
  return `${tokenType} ${normalizeToken(rawToken)}`;
};

const jsonHeaders = () => ({
  Authorization: getAuthHeader(),
  "Content-Type": "application/json",
  Accept: "application/json",
});

// ─── Mapping ──────────────────────────────────────────────────────────────────

const CATEGORY_MAP = {
  ALERT: "Alert",
  ACTION: "Action",
  APPROVAL: "Approval",
  UPDATE: "Update",
  SYSTEM: "System",
};

const toNotifCategory = (raw) =>
  CATEGORY_MAP[(raw || "").toUpperCase()] ?? "System";

/** Convert an uppercase UI category label back to the backend enum value. */
export const toBackendCategory = (category) =>
  category === "All" ? undefined : category.toUpperCase();

export const mapNotification = (dto) => ({
  id: dto.id,
  category: toNotifCategory(dto.category),
  title: dto.title,
  body: dto.message,
  projectId: dto.projectTag,
  projectName:
    (dto.metadata?.projectName ) ?? dto.projectTag,
  time: dto.createdAt,
  read: dto.read,
  link: dto.link,
});

const parseList = (payload) => {
  if (Array.isArray(payload)) return payload ;
  if (payload && typeof payload === "object") {
    const container = payload ;
    if (Array.isArray(container.data)) return container.data;
    if (Array.isArray(container.content)) return container.content ;
  }
  return [];
};

// ─── API calls ────────────────────────────────────────────────────────────────

/** GET /api/notifications/all (optionally filtered by category) */
export const fetchNotifications = async (
  category
) => {
  const backendCategory = category ? toBackendCategory(category) : undefined;
  const url = backendCategory
    ? `${NOTIFICATIONS_ALL_API_URL}?category=${encodeURIComponent(backendCategory)}`
    : NOTIFICATIONS_ALL_API_URL;

  const res = await apiFetch(url, { method: "GET", headers: jsonHeaders() }, "Failed to fetch notifications. Please try again.", { passthrough: [401] });
  console.log("fetchNotifications res:", res);
  if (!res.ok) {
    if (res.status === 401) throw new Error("Unauthorized. Please log in again.");
    throw new Error(`Failed to fetch notifications (${res.status})`);
  }
  const payload = await res.json();
  console.log("fetchNotifications payload:", payload);
  return parseList(payload).map(mapNotification);
};

/** GET /api/notifications/unread-count -> number */
export const fetchUnreadCount = async () => {
  const res = await apiFetch(NOTIFICATIONS_UNREAD_COUNT_API_URL, {
    method: "GET",
    headers: jsonHeaders(),
  }, "Failed to fetch unread count. Please try again.");
  if (!res.ok) throw new Error(`Failed to fetch unread count (${res.status})`);
  const data = (await res.json());
  console.log("fetchUnreadCount data:", data);
  return Number(data?.count ?? 0);
};

/** GET /api/notifications/counts -> per-category counts keyed by backend enum */
export const fetchCategoryCounts = async () => {
  const res = await apiFetch(NOTIFICATIONS_COUNTS_API_URL, {
    method: "GET",
    headers: jsonHeaders(),
  }, "Failed to fetch category counts. Please try again.");
  if (!res.ok) throw new Error(`Failed to fetch category counts (${res.status})`);
  return (await res.json()) ;
};

/** PATCH /api/notifications/read for a single notification */
export const markNotificationsRead = async (ids) => {
  const res = await apiFetch(NOTIFICATIONS_READ_API_URL, {
    method: "PATCH",
    headers: jsonHeaders(),
    body: JSON.stringify({ ids }),
  }, "Failed to mark notifications read. Please try again.");
  if (!res.ok) throw new Error(`Failed to mark notifications read (${res.status})`);
};

/** PATCH /api/notifications/read with { all: true } */
export const markAllNotificationsRead = async () => {
  const res = await apiFetch(NOTIFICATIONS_READ_API_URL, {
    method: "PATCH",
    headers: jsonHeaders(),
    body: JSON.stringify({ all: true }),
  }, "Failed to mark all notifications read. Please try again.");
  if (!res.ok) throw new Error(`Failed to mark all notifications read (${res.status})`);
};

/** DELETE /api/notifications/{id} */
export const deleteNotification = async (id) => {
  const res = await apiFetch(NOTIFICATION_DELETE_API_URL(id), {
    method: "DELETE",
    headers: jsonHeaders(),
  }, "Failed to delete notification. Please try again.");
  if (!res.ok && res.status !== 204) {
    throw new Error(`Failed to delete notification (${res.status})`);
  }
};
